import React, { FC } from 'react'
import { useTheme } from '@mui/material/styles'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import BrokenBowl from './broken-bowl'
import { Button } from '@mui/material'
import Link from '@/components/Link'
import { CustomError } from './errors'

const Container: FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={(theme) => ({
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      textAlign: 'center',
      [theme.breakpoints.down('sm')]: {
        position: 'relative',
        top: 0,
        transform: 'none',
        margin: '40px 0',
        padding: '0 40px',
      },
    })}
  >
    {children}
  </Box>
)

export type Props = {
  error: CustomError
  errorMessage: string
  showSearchVendorsCta?: boolean
  showEditBasketCta?: boolean
  showViewBasketCta?: boolean
  onChatToUsClick: () => void
}

const ErrorPage: FC<Props> = ({
  error,
  errorMessage,
  showSearchVendorsCta,
  showEditBasketCta,
  showViewBasketCta,
  onChatToUsClick,
}) => {
  const theme = useTheme()

  return (
    <Container>
      <BrokenBowl />
      <Stack spacing={2} direction="column">
        <Typography
          variant="h4"
          sx={{ color: theme.palette.text.primary, fontWeight: theme.typography.fontWeightMedium }}
        >
          This is awkward!
        </Typography>

        <Stack spacing={1} direction="column">
          <Typography variant="body1">
            {showViewBasketCta
              ? 'Some changes have been made to your basket'
              : "Something's gone wrong"}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              fontWeight: theme.typography.fontWeightMedium,
            }}
          >
            {errorMessage}
          </Typography>
        </Stack>

        <Stack spacing={4} direction="column">
          {showViewBasketCta && error.permalink && error.basketId ? (
            <Link href={`/office-catering/vendors/${error.permalink}/?basketId=${error.basketId}`}>
              <Button variant="contained">View basket</Button>
            </Link>
          ) : (
            <Stack spacing={1} direction="row">
              {error.ctaHref && (
                <Link href={error.ctaHref}>
                  <Button variant="contained">{error.ctaLabel || 'Take me back'}</Button>
                </Link>
              )}

              <Button
                variant={
                  error.ctaHref || showSearchVendorsCta || showEditBasketCta
                    ? 'outlined'
                    : 'contained'
                }
                onClick={() => window.location.reload()}
              >
                Try again
              </Button>

              {showSearchVendorsCta && (
                <Link href="/office-catering/vendors">
                  <Button variant="contained">Search vendors</Button>
                </Link>
              )}

              {showEditBasketCta && error.permalink && error.basketId && (
                <Link
                  href={`/office-catering/vendors/${error.permalink}/?basketId=${error.basketId}`}
                >
                  <Button variant="contained">Back to vendor</Button>
                </Link>
              )}
            </Stack>
          )}
          <Stack spacing={0.5} direction="row">
            <Typography variant="body2">Need help?</Typography>
            <Button size="small" onClick={onChatToUsClick}>
              Chat to us
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Container>
  )
}

export default ErrorPage
